import React, { useRef, useEffect } from 'react';
import { useTheme } from '@mui/material';
import { PurchaseOrder, PurchaseOrderItem } from '@domain/purchasing';
import { POItemGroups } from '@domain/purchasing/models/PurchaseOrderItemGroups';
import { useGenericDrawer } from '@shared/shell/drawer/useGenericDrawer';
import PurchaseOrderDetails from '../../details/PurchaseOrderDetails';
import { Paper } from '@shared/ui/common/paper';
import { useTimeline } from '@features/purchasing/hooks/useTimeline';
import {
    TimelineGrid,
    TimelineHeader,
    TimelineGroup,
    CurrentDateIndicator,
} from '.';
import { PurchaseOrderDraft } from '../../details/PurchaseOrderDraft';
import { useBottomSheet } from '@shared/shell/bottomsheet/useBottomSheet';
import { PurchaseOrderShipment } from '../../shipment/PurchaseOrderShipment';
import { usePurchaseOrderItems } from '@features/purchasing/hooks/usePurchaseOrderItems';

interface PurchaseOrdersTimelineProps {
    purchaseOrders?: PurchaseOrder[];
    groupedItems?: POItemGroups[];
    ungroupedItems?: PurchaseOrderItem[];
}

const PurchaseOrdersTimeline: React.FC<PurchaseOrdersTimelineProps> = ({
    groupedItems,
    ungroupedItems,
}) => {

    const theme = useTheme();

    const { updatePurchaseOrderItem } = usePurchaseOrderItems();

    const { openDrawer } = useGenericDrawer();
    const { openBottomSheet, closeBottomSheet } = useBottomSheet();

    const timelineRef = useRef<HTMLDivElement>(null);
    const weekWidth = 200;
    const headerColumnWidth = 300;
    const maxNumberOfWeeks = 52;

    // Use the timeline hook
    const { weeks, currentWeekIndex } = useTimeline({
        maxNumberOfWeeks,
        weekWidth,
        headerColumnWidth
    });

    // Auto-scroll to current week on mount
    useEffect(() => {
        if (timelineRef.current) {
            const scrollPosition = currentWeekIndex * weekWidth - timelineRef.current.clientWidth / 2 + headerColumnWidth;

            timelineRef.current.scrollTo({
                left: Math.max(0, scrollPosition),
                behavior: 'instant'
            });
        }
    }, [currentWeekIndex, weekWidth, headerColumnWidth]);

    const handleOrderClick = (order: PurchaseOrder) => {
        if (!order.id) {
            return;
        }

        if (order.draft) {
            openBottomSheet({
                title: 'Purchase Order Draft',
                component: <PurchaseOrderDraft purchaseOrderId={order.id} />,
            });
        } else {
            openDrawer(
                `Purchase Order ${order.poNumber || 'Details'}`,
                <PurchaseOrderDetails purchaseOrderId={order.id} />
            );
        }
    };

    const handleAddShipmentClick = (item: PurchaseOrderItem) => {
        openBottomSheet({
            title: 'Add Shipment',
            component: (
                <PurchaseOrderShipment
                    handleClose={closeBottomSheet}
                    handleFinish={() => closeBottomSheet()}
                    purchaseOrder={item.purchaseOrder!}
                    selectedShipment={item.shipment || undefined}
                    purchaseOrderItems={[item]}
                    products={item.product ? [item.product] : []}
                />
            )
        });
    }

    return (
        <Paper
            sx={{
                height: 'calc(100vh - 180px)',
                overflow: 'hidden',
            }}
        >

            <div
                ref={timelineRef}
                style={{
                    overflowX: 'auto',
                    overflowY: 'auto',
                    width: '100%',
                    height: '100%',
                    position: 'relative'
                }}
            >

                {/* Timeline content */}
                <div
                    style={{
                        minHeight: '100%',
                        display: 'flex',
                        position: 'relative',
                        flexDirection: 'column',
                        width: weekWidth * weeks.length + headerColumnWidth,
                    }}
                >

                    <TimelineGrid
                        weeks={weeks}
                        weekWidth={weekWidth}
                        headerColumnWidth={headerColumnWidth}
                    />

                    <TimelineHeader
                        weeks={weeks}
                        weekWidth={weekWidth}
                        headerColumnWidth={headerColumnWidth}
                    />

                    {groupedItems?.map((group, groupIndex) => (
                        <TimelineGroup
                            key={groupIndex}
                            group={group}
                            groupIndex={groupIndex}
                            headerColumnWidth={headerColumnWidth}
                            weeks={weeks}
                            weekWidth={weekWidth}
                            onOrderClick={handleOrderClick}
                            onAddShipmentClick={handleAddShipmentClick}
                        />
                    ))}

                    {/* Ungrouped items section */}
                    {ungroupedItems && ungroupedItems.length > 0 && (
                        <TimelineGroup
                            key="ungrouped"
                            group={{
                                id: 'ungrouped',
                                name: 'Ungrouped Items',
                                items: ungroupedItems
                            } as any}
                            onAddShipmentClick={handleAddShipmentClick}
                            groupIndex={groupedItems?.length || 0}
                            headerColumnWidth={headerColumnWidth}
                            weeks={weeks}
                            weekWidth={weekWidth}
                            onOrderClick={handleOrderClick}
                        />
                    )}

                    <CurrentDateIndicator
                        weekWidth={weekWidth}
                        headerColumnWidth={headerColumnWidth}
                    />

                </div >
            </div >
        </Paper >
    );
};

export default PurchaseOrdersTimeline; 